import React from "react";
import {
  Navigate,
  Route,
  createBrowserRouter,
  NonIndexRouteObject,
  IndexRouteObject,
  RouterProvider,
} from "react-router";
// import { RouterProvider } from 'react-router/dom';
import { AnyObject } from "@triare/auth-redux";
import Home from "../pages/Home";
import {
  paramsToString,
  routes as menuRoutes,
} from "../components/Layout/Menu";
import PrivateRoute from "./PrivateRoute";
import { useAuth, User } from "../store/auth";
import { Role } from "../enums/user";
import SignIn from "../pages/Auth/SignIn";
import { FormOutlined } from "@ant-design/icons";
import SignUp from "../pages/Auth/SignUp";
import Information from "../pages/Auth/SignUp/Information";
import ForgotPassword from "../pages/Auth/ForgotPassword";
import ResetPassword from "../pages/Auth/ResetPassword";
import ConfirmEmail from "../pages/Auth/ConfirmEmail";
import Dashboard from "../pages/Dashboard";
import Users from "../pages/Users";
import ChangePassword from "../pages/ChangePassword";
import Redirect from "../pages/Redirect";

export function generateDefaultUrl(user?: User | null) {
  return `${paramsToString(
    user,
    user && user?.role === Role.ADMIN ? undefined : getRouteParams("/", user),
  )}`;
}

export interface RouteElements {
  [role: string]: React.ReactNode | null;
}

interface RouteObjectExtension {
  elements?: RouteElements;
  params?: ((user: User) => string) | AnyObject;
}

interface MyIndexRouteObject extends IndexRouteObject, RouteObjectExtension { }

interface MyNonIndexRouteObject
  extends NonIndexRouteObject, RouteObjectExtension { }

export type RouteObject = MyIndexRouteObject | MyNonIndexRouteObject;
export type RouteRoles = (string | ((user: User) => boolean))[];

export interface Route {
  route?: RouteObject;
  roles?: RouteRoles;
  name?: string | ((user: User) => string);
  icon?: React.ReactNode | ((user: User) => React.ReactNode);
  parent?: Route;
  hidden?: boolean;
  onClick?: () => void;
  children?: Route[];
  outlet?: true;
  roleNeeded?: "user" | "manager" | "admin";
  privateRoute?: boolean;
  disabled?: string | ((user: User) => boolean);
  className?: string;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export function addChildrenRoutes(routes: Route[]): Route[] {
  const list: Route[] = [];

  const recursiveUpdate = (item: Route, parent?: Route): Route => {
    if (parent) {
      item.parent = parent;

      if (parent?.route?.children && item.route) {
        parent.route.children.push(item.route);
      }

      if (!item.outlet && item.route) {
        list.push(item);

        if (parent.route) {
          const childList = (parent.route?.children || []).filter(
            (child) => child !== item.route,
          );

          if (childList.length > 0) {
            Object.assign(parent.route, {
              children: childList,
            });
          } else {
            delete parent.route.children;
          }
        }

        Object.assign(item.route, {
          path: `${parent.route?.path}/${item.route?.path}`
            .replace(/\/+/, "/")
            .replace(/^\/+/, ""),
        });
      }
    }

    if (item.route) {
      const { route, children, privateRoute } = item;

      if (privateRoute && (route?.element || route?.elements)) {
        route.element = (
          <PrivateRoute
            element={route?.element}
            elements={route?.elements}
            item={item}
          />
        );
      }

      if (children) {
        route.children = [];

        for (const childRoute of children) recursiveUpdate(childRoute, item);
      }
    } else if (item.children) {
      for (const child of item.children.filter(({ route }) => route))
        list.push(recursiveUpdate(child));
    }

    return item;
  };

  return [...routes.map((route) => recursiveUpdate(route)), ...list].filter(
    ({ route }) => route,
  );
}
export function RootRedirect() {
  const { user } = useAuth();

  if (!user) return <Navigate to="/sign-in" replace />;

  if (user.role !== Role.ROOT) {
    return <Navigate to="/field-technicians" replace />;
  }

  return <Navigate to="/users" replace />;
}

export const routes: Route[] = addChildrenRoutes([
  {
    privateRoute: true,
    name: "Home",
    route: {
      path: "/",
      // element: <Home />,
      // element: <Navigate to="/users" replace />,
      element: <RootRedirect />,
    },
    children: [...menuRoutes],
  },
  {
    privateRoute: true,
    name: "Sign In",
    translationKey: "routes.signIn",
    align: "left",
    route: {
      path: "sign-in",
      element: <SignIn />,
    },
  },
  {
    name: "Sign Up",
    translationKey: "routes.signUp",
    icon: <FormOutlined />,
    align: "left",
    route: {
      path: "sign-up",
      element: <SignUp />,
    },
    children: [
      {
        ignoreMenu: true,
        name: "Information",
        route: {
          path: "information",
          element: <Information />,
        },
      },
    ],
  },
  {
    name: "Forgot password",
    translationKey: "routes.forgotPassword",
    align: "left",
    route: {
      path: "forgot-password",
      element: <ForgotPassword />,
    },
  },
  {
    name: "Reset password",
    translationKey: "routes.resetPassword",
    align: "left",
    route: {
      path: "reset-password/:secretKey",
      element: <ResetPassword />,
    },
  },
  {
    name: "Confirm email",
    translationKey: "routes.confirmEmail",
    align: "left",
    route: {
      path: "complete-registration/:mode/:secretKey",
      element: <ConfirmEmail />,
    },
  },
  {
    name: "Confirm email (set a password)",
    translationKey: "routes.confirmEmailSetPassword",
    align: "left",
    route: {
      path: "complete-registration-test/password/:secretKey",
      element: <ConfirmEmail mode="password" />,
    },
  },
  {
    name: "ChangePassword",
    // translationKey: "routes.users",
    align: "left",
    route: {
      path: "change-password",
      element: <ChangePassword />,
    },
  },
  {
    name: "Redirect",
    align: "left",
    route: {
      path: "redirect/:jobId",
      element: <Redirect />,
    },
  },
]);

const router = createBrowserRouter([
  ...routes.map(({ route }) => route as RouteObject),
  {
    path: "*",
    element: <Navigate to="/" replace />,
  },
]);

export function getRouteParams(path: string, user?: User | null): AnyObject {
  let params: ((user: User) => string) | AnyObject = {};

  routes.find(({ route }) => {
    if (route && route.path && route.path === path) {
      if (route.params) {
        params = route.params;
      }

      return true;
    }

    return false;
  });

  if (typeof params === "function") {
    if (user) {
      return params(user);
    }

    return {};
  }

  return params;
}

export default function Routes(): React.ReactNode {
  return <RouterProvider router={router} />;
}
